import { Component, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef, ViewContainerRef, Injector, ApplicationRef, EmbeddedViewRef, ViewEncapsulation } from "@angular/core";
import { MultiSourceMultiValueWidget } from "../../base/MultiSourceMultiValueWidget";
import { SharedPoint, SharedSeries } from "src/app/models/dashboard";
import * as L from 'leaflet';
import { layer } from "src/app/components/map/leaflet.kml";
import { getGradientColor, GradientStop } from "src/app/components/gradient-selector/gradient-selector.component";
import { IconService } from "src/service/icon.service";
import { FileService, DataService } from 'src/api/services';
import { AbstractDataService } from 'src/service/data.service';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { CommandService } from 'src/api/services/command.service';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { DashboardEventService } from '../../dashboard-event.service';
import { MapLayerTableService } from "src/service/map-layer.service";
import { DynamicTemplateComponent } from "src/app/components/dynamic-template/dynamic-template.component";
import * as Handlebars from 'handlebars/dist/cjs/handlebars';
import { TrendModule } from "ngx-trend";
import { DynamicLegend } from "./dynamic-legend";
import { CommonModule } from "@angular/common";
import SmoothMarkerBouncing from 'leaflet.smooth_marker_bouncing';
import { DashboardDataCacheService } from "src/service/dashboard-data-cache.service";
import { AzureAuthService } from "src/service/azure-auth.service";
SmoothMarkerBouncing(L);
@Component({
    selector: "app-telemetry-map-widget",
    templateUrl: "./telemetry-map-widget.component.html",
    styleUrls: ["./telemetry-map-widget.component.css"],
    encapsulation: ViewEncapsulation.None,
})
export class TelemetryMapWidgetComponent extends MultiSourceMultiValueWidget implements AfterViewInit {
    @ViewChild('mapContainer') private mapContainer!: ElementRef;
    private map: L.Map | undefined;
    public c: any;


    private centerLatitude: number;
    private centerLongitude: number;
    private zoomLevel: number;
    public defaultIcon: L.icon;

    private layersByUri: { [uri: string]: { layers: L.Layer[], originalStyles: any[] } } = {};

    private allBounds = L.latLngBounds([]);
    private loadedSeriesCount: number = 0;
    private totalSeriesCount: number = 0;

    private legend = new DynamicLegend();
    private layerGroups: Map<string, L.FeatureGroup> = new Map();
    private legendGroupsPopulated: Set<string> = new Set();
    private legendItemsPopulated: Set<string> = new Set(); // Track individual legend items
    private originalPinKeys: Map<string, string> = new Map();

    constructor(
        public override fileService: FileService,
        dataService: AbstractDataService,
        cd: ChangeDetectorRef,
        app: ApplicationConfigService,
        cmd: CommandService,
        toasterService: ToastrService,
        http: HttpClient,
        cookieService: CookieService,
        data: DataService,
        events: DashboardEventService,
        public icons: IconService,
        public mapLayerService: MapLayerTableService,
        private viewContainerRef: ViewContainerRef,
        private injector: Injector,
        private appRef: ApplicationRef,
        private _dashboardDataCache: DashboardDataCacheService,
        private auth: AzureAuthService
    ) {
            super(fileService, dataService, cd, app, cmd, toasterService, http, cookieService, data, events, auth, _dashboardDataCache);
    }

    ngAfterViewInit(): void {
        this.icons.getPins(pins => {
            this.defaultIcon = pins["blueSelected"];
            // this.selectedIcon = pins["blue"];

            L.Icon.Default.prototype.options.iconUrl = this.defaultIcon.options.iconUrl;
            L.Icon.Default.prototype.options.iconRetinaUrl = this.defaultIcon.options.iconRetinaUrl;
            L.Icon.Default.prototype.options.shadowUrl = this.defaultIcon.options.shadowUrl;
            L.Icon.Default.prototype.options.shadowRetinaUrl = this.defaultIcon.options.shadowRetinaUrl;
        });

        // Initialize map after view is ready
        this.centerLatitude = (this.config as any).centerLatitude || -25.5;
        this.centerLongitude = (this.config as any).centerLongitude || 133.5;
        this.zoomLevel = (this.config as any).zoomLevel || 4;

        // Initialize total count here BEFORE map loads so it's ready for data loading
        this.totalSeriesCount = (this.config as any).dynamicTimeSeriesList?.length || 0;
        this.initMap();
        this.loadLayers();

        // Leaflet calculates its container size at init time. In dashboard views the
        // container may not have its final dimensions yet (e.g. inside a tab, a hidden
        // panel, or a flex/grid layout still resolving). Calling invalidateSize() after
        // the browser has had a chance to finish layout corrects the tile offset/blank
        // tile bug that users work around by resizing the window.
        setTimeout(() => {
            this.map?.invalidateSize();
        }, 500);
    }

    private loadLayers() {
        for (let mapLayer of (this.config as any).layers) {
            if (mapLayer.active) {
                this.addMapLayerToMap(mapLayer.mapLayerId);
            }
        }

    }

    public refresh() {
        //todo: implement smarter refresh that only reloads data
    }

    toggleFullscreen() {
        this.fullScreen = !this.fullScreen;
        setTimeout(() => {
            this.map?.invalidateSize();
        }, 500);
    }

    override eventTypes: string[] = ["timeSeriesHover", "timeSeriesUnhover", "timeSeriesSelect", "timeSeriesUnselect"];
    override timeSeriesEvent(type: string, uri: string): void {
        const layerData = this.layersByUri[uri];
        if (!layerData) return;

        // Helper to identify our custom pulsing markers
        const isPulsingMarker = (layer: any): boolean => {
            return layer instanceof L.Marker &&
                layer.options?.icon?.options?.className === 'custom-div-icon';
        };

        // Helper to get the inner styling div (the child of the Leaflet wrapper)
        const getPulsingChild = (layer: L.Marker): HTMLElement | null => {
            const wrapper = layer.getElement();
            return wrapper ? (wrapper.firstElementChild as HTMLElement) : null;
        };

        if (type === 'timeSeriesHover') {
            layerData.layers.forEach(layer => {
                if (layer instanceof L.CircleMarker && !(layer instanceof L.Marker)) {
                    // Standard CircleMarker logic
                    layer.setStyle({ radius: 14, weight: 3 });
                    layer.bringToFront();
                }
                else if (layer instanceof L.Polyline && !(layer instanceof L.Polygon) && !(layer instanceof L.Marker)) {
                    layer.setStyle({ weight: 5, opacity: 1 });
                    layer.bringToFront();
                }
                else if (layer instanceof L.Marker) {
                    if (isPulsingMarker(layer)) {
                        // PULSING MARKER: Mimic CircleMarker hover (Scale up)
                        const child = getPulsingChild(layer);
                        if (child) {
                            // Apply CSS transition for smoothness
                            child.style.transition = 'transform 0.2s ease, border-color 0.2s';
                            // Scale 1.4x mimics increasing radius from 10 to 14
                            child.style.transform = 'scale(1.4)';
                        }
                        layer.setZIndexOffset(1000); // Bring to front
                    } else {
                        // STANDARD PIN: Bounce
                        (layer as any).setZIndexOffset(1000);
                        if (!(layer as any)._continuousBounce) {
                            (layer as any).bounce({ duration: 500, height: 50 });
                        }
                    }
                }
            });
        }
        else if (type === 'timeSeriesUnhover') {
            layerData.layers.forEach((layer, index) => {
                const originalStyle = layerData.originalStyles[index];

                if (layer instanceof L.CircleMarker && !(layer instanceof L.Marker)) {
                    layer.setStyle({ radius: originalStyle.radius, weight: originalStyle.weight });
                }
                else if (layer instanceof L.Polyline && !(layer instanceof L.Polygon) && !(layer instanceof L.Marker)) {
                    layer.setStyle({ weight: originalStyle.weight, opacity: originalStyle.opacity });
                }
                else if (layer instanceof L.Marker) {
                    if (isPulsingMarker(layer)) {
                        // PULSING MARKER: Reset scale
                        const child = getPulsingChild(layer);
                        if (child) {
                            child.style.transform = 'scale(1)';
                        }
                        layer.setZIndexOffset(0);
                    } else {
                        // STANDARD PIN: Reset
                        (layer as any).setZIndexOffset(0);
                        if ((layer as any)._continuousBounce) {
                            (layer as any).bounce();
                        }
                    }
                }
            });
        }
        else if (type === 'timeSeriesSelect') {
            layerData.layers.forEach(layer => {
                if (layer instanceof L.CircleMarker && !(layer instanceof L.Marker)) {
                    layer.setStyle({ weight: 4, color: '#FFD700' }); // Gold border
                    layer.bringToFront();
                }
                else if (layer instanceof L.Polyline && !(layer instanceof L.Polygon) && !(layer instanceof L.Marker)) {
                    layer.setStyle({ weight: 6, opacity: 1, color: '#FFD700' });
                    layer.bringToFront();
                }
                else if (layer instanceof L.Marker) {
                    if (isPulsingMarker(layer)) {
                        // PULSING MARKER: Mimic Select (Gold Border)
                        const child = getPulsingChild(layer);
                        if (child) {
                            child.style.borderColor = '#FFD700'; // Gold
                            child.style.borderWidth = '3px';     // Thicker
                        }
                        layer.setZIndexOffset(2000);
                    } else {
                        // STANDARD PIN: Standard select behavior
                        (layer as any).setZIndexOffset(2000);
                        // (layer as any).bounce();
                    }
                }
            });
        }
        else if (type === 'timeSeriesUnselect') {
            layerData.layers.forEach((layer, index) => {
                const originalStyle = layerData.originalStyles[index];

                if (layer instanceof L.CircleMarker && !(layer instanceof L.Marker)) {
                    layer.setStyle({
                        weight: originalStyle.weight,
                        color: originalStyle.color
                    });
                }
                else if (layer instanceof L.Polyline && !(layer instanceof L.Polygon) && !(layer instanceof L.Marker)) {
                    layer.setStyle({
                        weight: originalStyle.weight,
                        opacity: originalStyle.opacity,
                        color: originalStyle.color
                    });
                }
                else if (layer instanceof L.Marker) {
                    if (isPulsingMarker(layer)) {
                        // PULSING MARKER: Reset Border
                        const child = getPulsingChild(layer);
                        if (child) {
                            // Resetting to empty string removes inline styles,
                            // allowing the CSS class defaults (white border) to take over.
                            child.style.borderColor = '';
                            child.style.borderWidth = '';
                        }
                        layer.setZIndexOffset(0);
                    } else {
                        // STANDARD PIN: Reset
                        (layer as any).setZIndexOffset(0);
                        if ((layer as any)._continuousBounce) {
                            (layer as any).bounce();
                        } else {
                            (layer as any).stopBouncing();
                        }
                    }
                }
            });
        }
    }



    private initMap(): void {

        const legendMode = 'grouped';
        this.legend = new DynamicLegend({ mode: legendMode });
        this.legend.onGroupToggle = (groupName: string, isVisible: boolean) => {
            const targetGroup = this.layerGroups.get(groupName);
            if (targetGroup && this.map) {
                if (isVisible) {
                    this.map.addLayer(targetGroup);
                } else {
                    this.map.removeLayer(targetGroup);
                }
            }
        };

        const tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        });

        const googleTerrain = L.tileLayer('https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}', {
            maxZoom: 20,
            subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
        });

        const googleStreets = L.tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
            maxZoom: 20,
            subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
        });

        const googleSat = L.tileLayer('https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', {
            maxZoom: 20,
            subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
        });

        // Select the start layer based on mapType input
        let startLayer: L.TileLayer;
        switch ((this.config as any).mapType) {
            case 'terrain':
                startLayer = googleTerrain;
                break;
            case 'streets':
                startLayer = googleStreets;
                break;
            case 'satellite':
                startLayer = googleSat;
                break;
            case 'open':
            default:
                startLayer = tiles;
                break;
        }

        const baseMaps = {
            "OpenStreetMap": tiles,
            "Google Terrain": googleTerrain,
            "Google Streets": googleStreets,
            "Google Satellite": googleSat
        };

        // Initialize the map with settings
        this.map = L.map(this.mapContainer.nativeElement, {
            center: [this.centerLatitude, this.centerLongitude],
            zoom: this.zoomLevel,
            layers: [startLayer] // <--- Default layer only
        });

        // Add the Layer Control to the map
        L.control.layers(baseMaps).addTo(this.map);

        //Add scale bar if enabled
        if ((this.config as any).showScale) {
            L.control.scale({
                position: 'bottomleft',
                maxWidth: 200,
                imperial: false, // Only show metric units
                updateWhenIdle: true
            }).addTo(this.map);
        }

        // Add dynamic legend if enabled
        if ((this.config as any).showLegend) {
            (this.legend as L.Control).addTo(this.map);
        }


    }

    public override async loadData(): Promise<void> {
        this.clearMarkers();
        return super.loadData();
    }

    private clearMarkers(): void {
        // Clear all markers from feature groups
        this.layerGroups.forEach(group => group.clearLayers());

        // Remove any markers that were added directly to the map (not in a group)
        Object.values(this.layersByUri).forEach(layerData => {
            layerData.layers.forEach(layer => {
                if (this.map) {
                    this.map.removeLayer(layer);
                }
            });
        });

        // Reset tracking state
        this.layersByUri = {};
        this.allBounds = L.latLngBounds([]);
        this.legendGroupsPopulated = new Set();
        this.legendItemsPopulated = new Set();
        this.originalPinKeys = new Map();

        // Clear legend items
        this.legend.clear();
    }

    widgetBeforeInit(): void {
        this.c = <any>this.config;
        return;
    }

    widgetAfterInit(): void {
        /*const zoomToBounds = (this.config as any).zoomToBounds ?? false;

        if (zoomToBounds && this.allBounds.isValid()) {
            this.map!.fitBounds(this.allBounds, { padding: [50, 50] });
        } else if (!zoomToBounds) {
            console.warn('ZoomToBounds is false or undefined');
        } else if (!this.allBounds.isValid()) {
            console.warn('Bounds are not valid - no coordinates added');
        }*/
    }

    override widgetDataLoadComplete(): void {
        // After all data is loaded, check if we need to zoom to bounds
        this.doZoomToBounds();
    }

    widgetDataLoaded(s: SharedSeries) {
        if (!s) {
            //this.checkAndZoomToBounds();
            return;
        }

        // are s.latitude and s.longitude valid numbers and within valid ranges?
        if (isNaN(s.latitude) || isNaN(s.longitude) || s.latitude < -90 || s.latitude > 90 || s.longitude < -180 || s.longitude > 180) {
            console.warn('Invalid latitude or longitude for series', s);
            return;
        }

        const tsdi = (this.config as any).dynamicTimeSeriesList[s.index];

        //populate metadata dictionary
        const c = <any>this.config;
        if (c.dynamicTimeSeriesList[s.index].displayName == null || c.dynamicTimeSeriesList[s.index].displayName == '') {
            c.dynamicTimeSeriesList[s.index].displayName = s.name;
        }
        //this.metaDataDictionary[c.dynamicTimeSeriesList[s.index].displayName] = s.metaData;

        // Store original pinKey before rules might modify it
        const groupName = tsdi.displayName || "Default Group";
        if (!this.originalPinKeys.has(groupName) && tsdi.pinKey) {
            this.originalPinKeys.set(groupName, tsdi.pinKey);
        }

        if (tsdi.hideLayer) {
            // Check hide rules
            let showLayer = true;

            const latestPoint = s.values[s.values.length - 1];

            const rules = (tsdi.hideRules as any[]).sort((a, b) => (a.index || 0) - (b.index || 0));

            for (let rule of rules) {
                rule.value = this.parseForMetaTag(rule.value, s, latestPoint);
                let value = latestPoint.value;
                if (tsdi.useMetaTag && tsdi.valueMetaTag) {
                    if (latestPoint.metaData && tsdi.valueMetaTag in latestPoint.metaData) {
                        value = parseFloat(latestPoint.metaData[tsdi.valueMetaTag]);
                    }
                }
                if (rule.metaTag && rule.metaTag != '') {
                    // Check if the metaTag matches the pattern {tag} using regex
                    // If it does, parse it using the advanced parser to resolve from series/point metadata
                    const match = rule.metaTag.match(/\{(.+?)\}/);
                    if (match) {
                        const tag = match[1];

                        if (s.metaData && tag in s.metaData) {
                            value = s.metaData[tag];
                        }
                        else{
                            value = null;
                        }
                    } else {
                        // Simple metaTag lookup in point metadata
                        if (!latestPoint.metaData || !(rule.metaTag in latestPoint.metaData)) {
                            continue;
                        }
                        value = latestPoint.metaData[rule.metaTag];
                    }
                }

                if (rule.operator != 'stringEqual' && rule.operator != 'stringNotEqual') {
                    value = parseFloat(value);
                    rule.value = parseFloat(rule.value);
                    if (isNaN(value) || isNaN(rule.value)) {
                        continue;
                    }
                }

                // Check if the rule condition matches
                let conditionMet = false;
                switch (rule.operator) {
                    case 'lessThan':
                        conditionMet = value < rule.value;
                        break;
                    case 'lessThanEqual':
                        conditionMet = value <= rule.value;
                        break;
                    case 'greaterThan':
                        conditionMet = value > rule.value;
                        break;
                    case 'greaterThanEqual':
                        conditionMet = value >= rule.value;
                        break;
                    case 'equal':
                        conditionMet = value == rule.value;
                        break;
                    case 'notEqual':
                        conditionMet = value != rule.value;
                        break;
                    case 'stringEqual':
                        conditionMet = String(value).toUpperCase() === String(rule.value).toUpperCase();
                        break;
                    case 'stringNotEqual':
                        conditionMet = String(value).toUpperCase() !== String(rule.value).toUpperCase();
                        break;
                }

                // If condition is met, apply the hideType action
                if (conditionMet) {
                    if (rule.hideType === 'hide') {
                        showLayer = false;
                    }
                    else if (rule.hideType === 'show') {
                        tsdi.pinKey = rule.pinName;
                        showLayer = true;
                    }
                    else if (rule.hideType === 'bounce-slow') {
                        tsdi.bounce = true;
                        tsdi.bounceType = 'bounce-slow';
                    }
                    else if (rule.hideType === 'bounce-fast') {
                        tsdi.bounce = true;
                        tsdi.bounceType = 'bounce-fast';
                    }
                    else if (rule.hideType === 'pulse-slow') {
                        tsdi.pulse = true;
                        tsdi.pulseType = 'pulse-slow';
                    }
                    else if (rule.hideType === 'pulse-fast') {
                        tsdi.pulse = true;
                        tsdi.pulseType = 'pulse-fast';
                    }
                }
            }

            if (!showLayer) {
                //this.checkAndZoomToBounds(); // Still need to count this series
                return;
            }
        }

        if (tsdi.displayType === 'fixed') {
            this.addFixedToMap(s, tsdi);
        }
        else if (tsdi.displayType === 'tracker') {
            this.addTrackerToMap(s, tsdi);
        }

        //this.checkAndZoomToBounds();
        //this.doZoomToBounds();
    }

    widgetDataLoadTimeout(): void {
        throw new Error("Method not implemented.");
    }
    widgetLiveDataLoaded(point: any): void {
        throw new Error("Method not implemented.");
    }
    liveDataOnly(): boolean {
        return false;
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.map) {
            this.map.remove();
        }
    }

    /*private checkAndZoomToBounds(): void {
        this.loadedSeriesCount++;

        if (this.loadedSeriesCount === this.totalSeriesCount) {
            const zoomToBounds = (this.config as any).zoomToBounds ?? false;

            if (zoomToBounds && this.allBounds.isValid()) {
                this.map!.fitBounds(this.allBounds, { padding: [50, 50] });
            } else if (!zoomToBounds) {
                console.warn('ZoomToBounds is false or undefined');
            } else if (!this.allBounds.isValid()) {
                console.warn('Bounds are not valid - no coordinates added');
            }
        }
    }*/

    private doZoomToBounds(): void {
        console.log('Checking if we should zoom to bounds...');
        const zoomToBounds = (this.config as any).zoomToBounds ?? false;

        if (zoomToBounds && this.allBounds.isValid()) {
            this.map!.fitBounds(this.allBounds, { padding: [50, 50] });
        }
    }

    addFixedToMap(s: SharedSeries, tsdi: any) {
        // Determine colour, possibly using gradient
        if (!s.values || s.values.length === 0) {
            console.warn('No values for series', s);
        }
        // don't bother loading if not lat and lon set
        if (s.latitude == null || s.longitude == null) {
            console.warn('Latitude or Longitude is null for series', s);
            return;
        }
        if (isNaN(s.latitude) || isNaN(s.longitude)) {
            console.warn('Latitude or Longitude is NaN for series', s);
            return;
        }
        if (s.latitude < -90 || s.latitude > 90 || s.longitude < -180 || s.longitude > 180) {
            console.warn('Latitude or Longitude out of range for series', s);
            return;
        }

        if (tsdi.displayName) {
            tsdi.displayName = this.parseStringForMetaAdvanced(tsdi.displayName, s, s.values[s.values.length - 1]) || tsdi.displayName || s.name;
        }

        const groupName = tsdi.displayName || "Default Group";



        const conditionLabel = "Test Label";

        let colour = tsdi.colour || 'blue';
        if (tsdi.useGradient) {

            const latestPoint = s.values[s.values.length - 1];

            let value;

            if (tsdi.useMetaTag && tsdi.valueMetaTag) {
                if (latestPoint.metaData && tsdi.valueMetaTag in latestPoint.metaData) {
                    value = parseFloat(latestPoint.metaData[tsdi.valueMetaTag]);
                }
            }
            else {
                value = latestPoint.value;
            }

            const stops = tsdi.gradientStops as GradientStop[];

            for (let stop of stops) {
                //console.log('stop.number', stop.number);
                stop.number = this.parseForMetaTag(stop.number, s, latestPoint);
            }

            colour = getGradientColor(value, stops);
        }
        if (tsdi.renderType === 'circle') {
            let marker;
            if (!tsdi.pulse) {
                marker = L.circleMarker([s.latitude, s.longitude], {
                    color: colour,
                    fillColor: colour,
                    fillOpacity: 0.6,
                    radius: 10
                });
            }
            else {
                if (tsdi.pulseType === 'pulse-slow') {
                    const icon = this.createPulsingIcon(colour, 2);
                    marker = L.marker([s.latitude, s.longitude], { icon: icon });
                }
                else if (tsdi.pulseType === 'pulse-fast') {
                    const icon = this.createPulsingIcon(colour, 0.8);
                    marker = L.marker([s.latitude, s.longitude], { icon: icon });
                }

            }

            this.allBounds.extend([s.latitude, s.longitude]);

            if (tsdi.showPopups && tsdi.popupHTML) {
                let value = s.values[s.values.length - 1].value;
                const latestPoint = s.values[s.values.length - 1];
                if (tsdi.useMetaTag && tsdi.valueMetaTag) {

                    value = latestPoint.metaData[tsdi.valueMetaTag];
                }

                const popupData = {
                    series: s,
                    data: value,
                    latitude: s.latitude,
                    longitude: s.longitude,
                    metaData: s.metaData,
                    pointMetaData: latestPoint.metaData
                };
                const html = this.renderTemplate(tsdi.popupHTML, popupData);
                marker.bindPopup(html);
            }

            if (tsdi.tooltipTemplate) {
                const latestPoint = s.values[s.values.length - 1];
                let value = latestPoint.value;
                if (tsdi.useMetaTag && tsdi.valueMetaTag) {
                    value = latestPoint.metaData[tsdi.valueMetaTag];
                }
                const tooltipData = {
                    series: s,
                    data: value,
                    latitude: s.latitude,
                    longitude: s.longitude,
                    metaData: s.metaData,
                    pointMetaData: latestPoint.metaData
                };
                const tooltipText = this.renderTemplate(tsdi.tooltipTemplate, tooltipData);
                marker.bindTooltip(tooltipText, { direction: 'top', offset: [0, -10], opacity: 0.8 });
            }

            // i want to bind to the click event of the circle marker here
            marker.on('click', (e) => {
                console.log('Circle marker clicked', e);
                // Add your custom click event logic here
                (marker as any).selected = !(marker as any).selected;
                this.events.trigger((marker as any).selected ? 'timeSeriesSelect' : 'timeSeriesUnselect', s.uri, e);
                // make sure the event propagates

                return e;
            });
            marker.on('mouseover', (e) => {
                console.log('Circle marker clicked', e);
                this.events.trigger('timeSeriesHover', s.uri, e);
                return e;
            });
            marker.on('mouseout', (e) => {
                console.log('Circle marker clicked', e);
                this.events.trigger('timeSeriesUnhover', s.uri, e);
                return e;
            });

            // Add marker to appropriate layer (group or map)
            const shouldGroup = tsdi.groupInLegend !== false;

            if (shouldGroup) {
                if (!this.layerGroups.has(groupName)) {
                    const newLayerGroup = L.featureGroup();
                    // Only add to map if showGroupByDefault !== false
                    if (tsdi.showGroupByDefault !== false) {
                        newLayerGroup.addTo(this.map!);
                    }
                    this.layerGroups.set(groupName, newLayerGroup);
                }
                const targetLayerGroup = this.layerGroups.get(groupName)!;

                try {
                    marker.addTo(targetLayerGroup);
                } catch {
                    console.error('Error adding marker to map', marker, this.map);
                }
            } else {
                // Individual item - add directly to map (no grouping)
                marker.addTo(this.map!);
            }

            // Store layer reference for events
            this.layersByUri[tsdi.uri] = {
                layers: [marker],
                originalStyles: [{ radius: 10, weight: 1, color: colour }]
            };

            if ((this.config as any).showLegend) {
                const shouldGroup = tsdi.groupInLegend !== false; // Default to true if undefined

                if (tsdi.useGradient && tsdi.gradientStops && tsdi.gradientStops.length > 0) {
                    if (shouldGroup) {
                        // Grouped mode: show all gradient stops
                        const sortedStops = [...tsdi.gradientStops].sort((a: any, b: any) => {
                            const posA = a.position !== undefined ? a.position : 0;
                            const posB = b.position !== undefined ? b.position : 0;
                            return posA - posB;
                        });

                        sortedStops.forEach((stop: any) => {
                            // Map the label from the config, fallback to the raw number boundary if undefined
                            const latestPoint = s.values[s.values.length - 1];
                            const stopLabel = this.parseForMetaTag(stop.label, s, latestPoint) || stop.label || `${stop.number}`;
                            //const stopLabel = stop.label || `${stop.number}`;

                            // Handle variance in color property naming conventions if necessary
                            const stopColor = stop.color || stop.colour || '#000';

                            this.legend.addItem(
                                stopLabel,
                                this.getCircleHtml(stopColor, stopColor),
                                groupName,
                                tsdi.showGroupByDefault !== false
                            );
                        });
                    } else {
                        // Individual mode: show single entry with current color
                        const itemKey = `${tsdi.uri}_circle`;
                        if (!this.legendItemsPopulated.has(itemKey)) {
                            this.legendItemsPopulated.add(itemKey);
                            this.legend.addItem(
                                tsdi.displayName || s.name,
                                this.getCircleHtml(colour, colour)
                            );
                        }
                    }
                } else {
                    // Handle non-gradient layers (static color)
                    const color = tsdi.colour || 'blue';
                    if (shouldGroup) {
                        this.legend.addItem(
                            tsdi.displayName || s.name,
                            this.getCircleHtml(color, color),
                            groupName,
                            tsdi.showGroupByDefault !== false
                        );
                    } else {
                        // Individual mode: show single entry
                        const itemKey = `${tsdi.uri}_circle`;
                        if (!this.legendItemsPopulated.has(itemKey)) {
                            this.legendItemsPopulated.add(itemKey);
                            this.legend.addItem(
                                tsdi.displayName || s.name,
                                this.getCircleHtml(color, color)
                            );
                        }
                    }
                }
            }

        }
        else if (tsdi.renderType === 'pin') {
            //add pin marker
            let icon = this.defaultIcon;
            if (tsdi.pinKey && tsdi.pinKey in this.icons.pins) {
                icon = this.icons.pins[tsdi.pinKey];
                //console.log('Using pin icon', icon);
            }
            const marker = L.marker([s.latitude, s.longitude], {
                icon: icon
            });

            if (tsdi.bounce) {
                if (tsdi.bounceType === 'bounce-slow') {
                    marker.setBouncingOptions({ exclusive: false, bounces: Infinity, bounceSpeed: 130, bounceHeight: 20, loop: true });
                }
                else if (tsdi.bounceType === 'bounce-fast') {
                    marker.setBouncingOptions({ exclusive: false, bounces: Infinity, bounceSpeed: 20, bounceHeight: 20, loop: true });
                }
                marker.bounce();
                (marker as any)._continuousBounce = true;
            }

            this.allBounds.extend([s.latitude, s.longitude]);

            if (tsdi.showPopups && tsdi.popupHTML) {

                let value = s.values[s.values.length - 1].value;
                if (tsdi.useMetaTag && tsdi.valueMetaTag) {
                    const latestPoint = s.values[s.values.length - 1];
                    value = latestPoint.metaData[tsdi.valueMetaTag];
                }

                const popupData = {
                    series: s,
                    data: value,
                    latitude: s.latitude,
                    longitude: s.longitude,
                    metaData: s.metaData
                };
                const html = this.renderTemplate(tsdi.popupHTML, popupData);
                marker.bindPopup(html);
            }

            if (tsdi.tooltipTemplate) {

                let value = s.values[s.values.length - 1].value;

                const popupData = {
                    series: s,
                    data: value,
                    latitude: s.latitude,
                    longitude: s.longitude,
                    metaData: s.metaData
                };

                if (tsdi.tooltipTemplate) {
                    const tooltipText = this.renderTemplate(tsdi.tooltipTemplate, popupData);
                    marker.bindTooltip(tooltipText, { direction: 'top', offset: [0, -10], opacity: 0.8 });
                }
            }

            // i want to bind to the click event of the circle marker here
            marker.on('click', (e) => {
                // Add your custom click event logic here
                (marker as any).selected = !(marker as any).selected;
                this.events.trigger((marker as any).selected ? 'timeSeriesSelect' : 'timeSeriesUnselect', s.uri, e);
                // make sure the event propagates

                return e;
            });
            marker.on('mouseover', (e) => {
                this.events.trigger('timeSeriesHover', s.uri, e);
                return e;
            });
            marker.on('mouseout', (e) => {
                this.events.trigger('timeSeriesUnhover', s.uri, e);
                return e;
            });

            // Add marker to appropriate layer (group or map)
            const shouldGroup = tsdi.groupInLegend !== false;
            const groupName = tsdi.displayName || "Default Group";

            if (shouldGroup) {
                // Create layer group if it doesn't exist
                if (!this.layerGroups.has(groupName)) {
                    const newLayerGroup = L.featureGroup();
                    // Only add to map if showGroupByDefault !== false
                    if (tsdi.showGroupByDefault !== false) {
                        newLayerGroup.addTo(this.map!);
                    }
                    this.layerGroups.set(groupName, newLayerGroup);
                }
                const targetLayerGroup = this.layerGroups.get(groupName)!;
                marker.addTo(targetLayerGroup);
            } else {
                marker.addTo(this.map!);
            }

            // Store layer reference for events
            this.layersByUri[tsdi.uri] = {
                layers: [marker],
                originalStyles: [{}]
            };

            if ((this.config as any).showLegend) {
                const shouldGroup = tsdi.groupInLegend !== false; // Default to true if undefined
                const groupName = tsdi.displayName || "Default Group";

                if (shouldGroup) {
                    // Only populate legend for this group once
                    if (!this.legendGroupsPopulated.has(groupName)) {
                        this.legendGroupsPopulated.add(groupName);

                        const pinsToShow: Array<{ pinKey: string, label: string }> = [];

                        // Add base pin with 'Default' label - use original pinKey before rules modified it
                        const basePinKey = this.originalPinKeys.get(groupName) || tsdi.pinKey || 'blueSelected';

                        const label = tsdi.defaultPinName ? this.parseForMetaTag(tsdi.defaultPinName, s, s.values[s.values.length - 1]) || tsdi.defaultPinName : 'Default';
                        pinsToShow.push({ pinKey: basePinKey, label: label });

                        // Collect all pins that could be shown from hideRules
                        if (tsdi.hideLayer && tsdi.hideRules) {
                            for (let rule of tsdi.hideRules) {
                                if (rule.hideType === 'show' && rule.pinName) {
                                    //const label = rule.label || rule.pinName;
                                    const latestPoint = s.values[s.values.length - 1];
                                    const label = this.parseForMetaTag(rule.label, s, latestPoint) || rule.label || `${rule.number}`;
                                    pinsToShow.push({ pinKey: rule.pinName, label: label });
                                }
                            }
                        }

                        // Add each unique pin to the legend under the group
                        pinsToShow.forEach(item => {
                            if (item.pinKey in this.icons.pins) {
                                const pinIcon = this.icons.pins[item.pinKey];
                                this.legend.addItem(item.label, this.getMarkerHtml(pinIcon.options.iconUrl), groupName, tsdi.showGroupByDefault !== false);
                            }
                        });
                    }
                } else {
                    // Individual mode: show single entry using current icon
                    const itemKey = `${tsdi.uri}_pin`;
                    if (!this.legendItemsPopulated.has(itemKey)) {
                        this.legendItemsPopulated.add(itemKey);
                        this.legend.addItem(tsdi.displayName || s.name, this.getMarkerHtml(icon.options.iconUrl));
                    }
                }

            }
        }
        else if (tsdi.renderType === 'geojson' && tsdi.geoJsonLayer) {


            this.mapLayerService.getLayerByName(tsdi.geoJsonLayer).subscribe(layer => {
                if (layer && layer.url) {
                    const url = layer.url;

                    fetch(url)
                        .then(response => {
                            if (!response.ok) {
                                throw new Error(`HTTP error! Status: ${response.status}`);
                            }
                            return response.json();
                        })
                        .then(geoJsonData => {
                            const geoJsonLayer = L.geoJSON(geoJsonData, {
                                style: (feature) => {
                                    return {
                                        color: colour,
                                        fillColor: colour,
                                        fillOpacity: 0.6,
                                        weight: 2
                                    };
                                },
                                pointToLayer: (feature, latlng) => {
                                    this.allBounds.extend(latlng);
                                    return L.circleMarker(latlng, {
                                        color: colour,
                                        fillColor: colour,
                                        fillOpacity: 0.6,
                                        radius: 10
                                    });
                                },
                                onEachFeature: (feature, layer) => {
                                    // For non-point features, extend bounds based on layer bounds
                                    if (layer.getBounds) {
                                        const layerBounds = layer.getBounds();
                                        this.allBounds.extend(layerBounds);
                                    }

                                    const latestPoint = s.values[s.values.length - 1];
                                    let value = latestPoint.value;
                                    if (tsdi.useMetaTag && tsdi.valueMetaTag) {
                                        value = latestPoint.metaData[tsdi.valueMetaTag];
                                    }

                                    if (tsdi.showPopups && tsdi.popupHTML) {
                                        const popupData = {
                                            series: s,
                                            data: value,
                                            latitude: s.latitude,
                                            longitude: s.longitude,
                                            metaData: feature.properties || {}
                                        };
                                        const html = this.renderTemplate(tsdi.popupHTML, popupData);
                                        layer.bindPopup(html);
                                    }

                                    if (tsdi.tooltipTemplate) {
                                        const tooltipData = {
                                            series: s,
                                            data: value,
                                            latitude: s.latitude,
                                            longitude: s.longitude,
                                            metaData: feature.properties || {}
                                        };
                                        const tooltipText = this.renderTemplate(tsdi.tooltipTemplate, tooltipData);
                                        layer.bindTooltip(tooltipText, { direction: 'top', offset: [0, -10], opacity: 0.8 });
                                    }
                                }
                            })

                            // Add GeoJSON layer to appropriate layer (group or map)
                            const shouldGroup = tsdi.groupInLegend !== false;
                            const groupName = tsdi.displayName || "Default Group";

                            if (shouldGroup) {
                                // Create layer group if it doesn't exist
                                if (!this.layerGroups.has(groupName)) {
                                    const newLayerGroup = L.featureGroup();
                                    // Only add to map if showGroupByDefault !== false
                                    if (tsdi.showGroupByDefault !== false) {
                                        newLayerGroup.addTo(this.map!);
                                    }
                                    this.layerGroups.set(groupName, newLayerGroup);
                                }
                                const targetLayerGroup = this.layerGroups.get(groupName)!;
                                geoJsonLayer.addTo(targetLayerGroup);
                            } else {
                                geoJsonLayer.addTo(this.map!);
                            }

                            // Store layer reference so clearMarkers() can remove it on reload
                            this.layersByUri[tsdi.uri] = {
                                layers: [geoJsonLayer],
                                originalStyles: [{}]
                            };

                            if ((this.config as any).showLegend) {
                                const shouldGroup = tsdi.groupInLegend !== false;
                                const groupName = tsdi.displayName || "Default Group";
                                if (shouldGroup) {
                                    this.legend.addItem(
                                        tsdi.displayName,
                                        this.getBoxHtml(colour, colour),
                                        groupName,
                                        tsdi.showGroupByDefault !== false
                                    );
                                } else {
                                    const itemKey = `${tsdi.uri}_geojson`;
                                    if (!this.legendItemsPopulated.has(itemKey)) {
                                        this.legendItemsPopulated.add(itemKey);
                                        this.legend.addItem(
                                            tsdi.displayName || s.name,
                                            this.getBoxHtml(colour, colour)
                                        );
                                    }
                                }
                            }

                            // Re-zoom after GeoJSON is loaded (without incrementing counter)
                            this.doZoomToBounds();
                        })
                        .catch(error => {
                            console.error("Error loading GeoJSON:", error);
                        });

                } else {
                    // This handles if the layer wasn't found in the service
                    console.warn(`Layer '${tsdi.geoJsonLayer}' not found or missing URL.`);
                }
            });
        }
    }

    addTrackerToMap(s: SharedSeries, tsdi: any) {
        let legendAdded = false;
        if (tsdi.renderType === 'pin') {
            let icon = this.defaultIcon;
            if (tsdi.pinKey && tsdi.pinKey in this.icons.pins) {
                icon = this.icons.pins[tsdi.pinKey];
            }

            for (let v of (s.values as any)) {
                if (!v.coordinate || !v.coordinate.latitude || !v.coordinate.longitude) {
                    console.warn("Skipping invalid tracker value");
                    continue;
                }
                let value;
                if (tsdi.useMetaTag && tsdi.valueMetaTag) {
                    if (v.metaData && tsdi.valueMetaTag in v.metaData) {
                        value = parseFloat(v.metaData[tsdi.valueMetaTag]);
                    }
                    else {
                        continue;
                    }
                }
                else {
                    value = v.value;
                }
                //A tracker coordinate doesn't necessarily have a value?
                // if (value == undefined) {
                //     continue;
                // }

                const marker = L.marker([v.coordinate.latitude, v.coordinate.longitude], {
                    icon: icon
                })

                this.allBounds.extend([v.coordinate.latitude, v.coordinate.longitude]);

                if (tsdi.showPopups && tsdi.popupHTML) {
                    const popupData = {
                        series: s,
                        data: value,
                        latitude: v.coordinate.latitude,
                        longitude: v.coordinate.longitude,
                        metaData: v.metaData,
                        coordinate: v.coordinate
                    };

                    const html = this.renderTemplate(tsdi.popupHTML, popupData);
                    marker.bindPopup(html);
                }

                if (tsdi.tooltipTemplate) {
                    const tooltipData = {
                        series: s,
                        data: value,
                        latitude: v.coordinate.latitude,
                        longitude: v.coordinate.longitude,
                        metaData: v.metaData,
                        coordinate: v.coordinate
                    };
                    const tooltipText = this.renderTemplate(tsdi.tooltipTemplate, tooltipData);
                    marker.bindTooltip(tooltipText, { direction: 'top', offset: [0, -10], opacity: 0.8 });
                }

                if (tsdi.bounce) {
                    if (tsdi.bounceType === 'bounce-slow') {
                        marker.setBouncingOptions({ exclusive: false, bounces: Infinity, bounceSpeed: 130, bounceHeight: 20, loop: true });
                    }
                    else if (tsdi.bounceType === 'bounce-fast') {
                        marker.setBouncingOptions({ exclusive: false, bounces: Infinity, bounceSpeed: 20, bounceHeight: 20, loop: true });
                    }
                    marker.bounce();
                }

                // Add marker to appropriate layer (group or map)
                const shouldGroup = tsdi.groupInLegend !== false;
                const groupName = tsdi.displayName || "Default Group";

                if (shouldGroup) {
                    // Create layer group if it doesn't exist
                    if (!this.layerGroups.has(groupName)) {
                        const newLayerGroup = L.featureGroup();
                        if (tsdi.showGroupByDefault !== false) {
                            newLayerGroup.addTo(this.map!);
                        }
                        this.layerGroups.set(groupName, newLayerGroup);
                    }
                    const targetLayerGroup = this.layerGroups.get(groupName)!;
                    marker.addTo(targetLayerGroup);
                } else {
                    marker.addTo(this.map!);
                }

                if (!legendAdded && (this.config as any).showLegend) {
                    this.legend.addItem(
                        tsdi.displayName,
                        this.getMarkerHtml(icon.options.iconUrl),
                        shouldGroup ? groupName : undefined,
                        tsdi.showGroupByDefault !== false
                    );
                    legendAdded = true;
                }
            }
        }
        else if (tsdi.renderType === 'circle') {

            const latestPoint = s.values[s.values.length - 1];
            const stops = tsdi.gradientStops as GradientStop[];

            for (let stop of stops) {
                console.log('stop.number', stop.number);
                stop.number = this.parseForMetaTag(stop.number, tsdi);
            }

            const trackerLayers: L.Layer[] = [];
            const trackerStyles: any[] = [];
            
            console.debug('Total number of tracker values to process:', (s.values as any).length);
            for (let v of (s.values as any)) {
                if (!v.coordinate || !v.coordinate.latitude || !v.coordinate.longitude) {
                    console.warn("Skipping invalid tracker value: " + JSON.stringify(v));
                    continue;
                }
                let colour = tsdi.colour || 'blue';
                let value;
                if (tsdi.useGradient) {

                    if (tsdi.useMetaTag && tsdi.valueMetaTag) {
                        if (v.metaData && tsdi.valueMetaTag in v.metaData) {
                            value = parseFloat(v.metaData[tsdi.valueMetaTag]);
                        }
                        else {
                            continue;
                        }
                    }
                    else {
                        value = v.value;
                    }
                    if (value == undefined) {
                        continue;
                    }

                    colour = getGradientColor(value, stops);
                }

                let marker;
                if (!tsdi.pulse) {
                    marker = L.circleMarker([v.coordinate.latitude, v.coordinate.longitude], {
                        color: colour,
                        fillColor: colour,
                        fillOpacity: 0.6,
                        radius: 10
                    });
                }
                else {
                    if (tsdi.pulseType === 'pulse-slow') {
                        const icon = this.createPulsingIcon(colour, 2);
                        marker = L.marker([v.coordinate.latitude, v.coordinate.longitude], { icon: icon });
                    }
                    else if (tsdi.pulseType === 'pulse-fast') {
                        const icon = this.createPulsingIcon(colour, 0.8);
                        marker = L.marker([v.coordinate.latitude, v.coordinate.longitude], { icon: icon });
                    }

                }

                if ("coordinate" in v && v.coordinate && v.coordinate.latitude && v.coordinate.longitude) {
                    this.allBounds.extend([v.coordinate.latitude, v.coordinate.longitude]);
                }
                else{
                    continue;
                }

                if (tsdi.showPopups && tsdi.popupHTML) {
                    const popupData = {
                        series: s,
                        data: value,
                        latitude: v.coordinate.latitude,
                        longitude: v.coordinate.longitude,
                        metaData: v.metaData,
                        coordinate: v.coordinate
                    };

                    const html = this.renderTemplate(tsdi.popupHTML, popupData);
                    marker.bindPopup(html);
                }

                if (tsdi.tooltipTemplate) {
                    const tooltipData = {
                        series: s,
                        data: value,
                        latitude: v.coordinate.latitude,
                        longitude: v.coordinate.longitude,
                        metaData: v.metaData,
                        coordinate: v.coordinate
                    };
                    const tooltipText = this.renderTemplate(tsdi.tooltipTemplate, tooltipData);
                    marker.bindTooltip(tooltipText, { direction: 'top', offset: [0, -10], opacity: 0.8 });
                }

                // Add marker to appropriate layer (group or map)
                const shouldGroup = tsdi.groupInLegend !== false;
                const groupName = tsdi.displayName || "Default Group";

                if (shouldGroup) {
                    // Create layer group if it doesn't exist
                    if (!this.layerGroups.has(groupName)) {
                        const newLayerGroup = L.featureGroup();
                        if (tsdi.showGroupByDefault !== false) {
                            newLayerGroup.addTo(this.map!);
                        }
                        this.layerGroups.set(groupName, newLayerGroup);
                    }
                    const targetLayerGroup = this.layerGroups.get(groupName)!;
                    marker.addTo(targetLayerGroup);
                } else {
                    marker.addTo(this.map!);
                }

                trackerLayers.push(marker);
                trackerStyles.push({ radius: 10, weight: 1, color: colour });

                if (!legendAdded && (this.config as any).showLegend) {
                    this.legend.addItem(
                        tsdi.displayName,
                        this.getCircleHtml(colour, colour),
                        shouldGroup ? groupName : undefined,
                        tsdi.showGroupByDefault !== false
                    );
                    legendAdded = true;
                }
            }

            // Store all tracker layers for this series
            if (trackerLayers.length > 0) {
                this.layersByUri[tsdi.uri] = {
                    layers: trackerLayers,
                    originalStyles: trackerStyles
                };
            }
        }
        else if (tsdi.renderType === 'path') {
            let colour = tsdi.colour || 'blue';
            const pathCoordinates: L.LatLngExpression[] = [];

            for (let v of (s.values as any)) {
                if (v.coordinate && v.coordinate.latitude && v.coordinate.longitude) {
                    pathCoordinates.push([v.coordinate.latitude, v.coordinate.longitude]);
                    this.allBounds.extend([v.coordinate.latitude, v.coordinate.longitude]);
                }
            }

            if (pathCoordinates.length > 1) {
                const polyline = L.polyline(pathCoordinates, {
                    color: colour,
                    weight: 3,
                    opacity: 0.7
                })

                // Add polyline to appropriate layer (group or map)
                const shouldGroup = tsdi.groupInLegend !== false;
                const groupName = tsdi.displayName || "Default Group";

                if (shouldGroup) {
                    // Create layer group if it doesn't exist
                    if (!this.layerGroups.has(groupName)) {
                        const newLayerGroup = L.featureGroup();
                        if (tsdi.showGroupByDefault !== false) {
                            newLayerGroup.addTo(this.map!);
                        }
                        this.layerGroups.set(groupName, newLayerGroup);
                    }
                    const targetLayerGroup = this.layerGroups.get(groupName)!;
                    polyline.addTo(targetLayerGroup);
                } else {
                    polyline.addTo(this.map!);
                }

                // Store layer reference for events
                this.layersByUri[tsdi.uri] = {
                    layers: [polyline],
                    originalStyles: [{ weight: 3, opacity: 0.7, color: colour }]
                };

                if (!legendAdded && (this.config as any).showLegend) {
                    this.legend.addItem(
                        tsdi.displayName,
                        this.getLineHtml(colour, 3),
                        shouldGroup ? groupName : undefined,
                        tsdi.showGroupByDefault !== false
                    );
                    legendAdded = true;
                }
            }
        }
    }

    addMapLayerToMap(mapLayerId: number) {
        this.mapLayerService.getLayerById(mapLayerId).subscribe(layerItem => {
            if (!layerItem || !layerItem.url) {
                console.warn(`Layer with ID ${mapLayerId} not found or missing URL`);
                return;
            }

            const layerType = layerItem.layerType.toLowerCase();

            switch (layerType) {
                case 'kml':
                    this.addKmlLayer(layerItem);
                    break;
                case 'geojson':
                    this.addGeoJsonLayer(layerItem);
                    break;
                case 'wms':
                    this.addWmsLayer(layerItem);
                    break;
                case 'tilemap':
                    this.addTileMapLayer(layerItem);
                    break;
                case 'esri':
                    this.addEsriLayer(layerItem);
                    break;
                default:
                    console.warn(`Unsupported layer type: ${layerItem.layerType}`);
            }
        });
    }

    private addKmlLayer(layerItem: any) {
        fetch(layerItem.url)
            .then(response => response.text())
            .then(kmlText => {
                const parser = new DOMParser();
                const kmlDoc = parser.parseFromString(kmlText, 'text/xml');
                const kmlLayer = layer(kmlDoc);
                kmlLayer.addTo(this.map!);

                if (layerItem.visible && this.map && kmlLayer.getBounds) {
                    this.allBounds.extend(kmlLayer.getBounds());
                }
            })
            .catch(error => {
                console.error(`Error loading KML layer ${layerItem.layerName}:`, error);
            });
    }

    private addGeoJsonLayer(layerItem: any) {
        fetch(layerItem.url)
            .then(response => response.json())
            .then(geoJsonData => {
                const geoJsonLayer = L.geoJSON(geoJsonData, {
                    style: {
                        color: '#3388ff',
                        weight: 2,
                        fillOpacity: 0.4
                    }
                });
                geoJsonLayer.addTo(this.map!);

                if (layerItem.visible && this.map && geoJsonLayer.getBounds) {
                    this.allBounds.extend(geoJsonLayer.getBounds());
                }
            })
            .catch(error => {
                console.error(`Error loading GeoJSON layer ${layerItem.layerName}:`, error);
            });
    }

    private addWmsLayer(layerItem: any) {
        const wmsLayer = L.tileLayer.wms(layerItem.url, {
            layers: layerItem.fileLayerName || '',
            format: 'image/png',
            transparent: true,
            attribution: layerItem.layerName
        });
        wmsLayer.addTo(this.map!);
    }

    private addTileMapLayer(layerItem: any) {
        const tileLayer = L.tileLayer(layerItem.url, {
            maxZoom: 19,
            attribution: layerItem.layerName,
            tileSize: layerItem.tileSize || 256
        });
        tileLayer.addTo(this.map!);
    }

    private addEsriLayer(layerItem: any) {
        // Check if this is a FeatureServer or MapServer
        if (layerItem.url.includes('FeatureServer')) {
            // FeatureServer - query features as GeoJSON
            const queryUrl = `${layerItem.url}/query?where=1=1&outFields=*&f=geojson`;

            fetch(queryUrl)
                .then(response => response.json())
                .then(geoJsonData => {
                    const esriLayer = L.geoJSON(geoJsonData, {
                        style: {
                            color: '#3388ff',
                            weight: 2,
                            fillOpacity: 0.4
                        },
                        pointToLayer: (feature, latlng) => {
                            return L.circleMarker(latlng, {
                                radius: 8,
                                fillColor: '#3388ff',
                                color: '#000',
                                weight: 1,
                                opacity: 1,
                                fillOpacity: 0.8
                            });
                        },
                        onEachFeature: (feature, layer) => {
                            // Add popup with feature properties if available
                            if (feature.properties) {
                                const popupContent = Object.entries(feature.properties)
                                    .map(([key, value]) => `<strong>${key}:</strong> ${value}`)
                                    .join('<br>');
                                layer.bindPopup(popupContent);
                            }
                        }
                    });
                    esriLayer.addTo(this.map!);

                    if (layerItem.visible && this.map && esriLayer.getBounds) {
                        this.allBounds.extend(esriLayer.getBounds());
                    }
                })
                .catch(error => {
                    console.error(`Error loading ESRI FeatureServer layer ${layerItem.layerName}:`, error);
                });
        } else {
            // MapServer - use tile layer format
            const esriLayer = L.tileLayer(layerItem.url + '/tile/{z}/{y}/{x}', {
                maxZoom: 19,
                attribution: layerItem.layerName
            });
            esriLayer.addTo(this.map!);
        }
    }



    private getCircleHtml(border: string, fill: string): string {
        const fillWithOpacity = fill.startsWith('#') && fill.length === 7 ? fill + 'B3' : fill;
        return `<i style="border: 2px solid ${border}; background: ${fillWithOpacity}; border-radius: 50%; width: 12px; height: 12px; display: inline-block;"></i>`;
    }

    private getBoxHtml(border: string, fill: string): string {
        return `
        <i style="
        border: 2px solid ${border};
        background-color: ${fill};
        opacity: 0.7;
        width: 12px;
        height: 12px;
        display: inline-block;
        box-sizing: border-box;
        "></i>`;
    }

    private getMarkerHtml(iconUrl: string): string {
        //console.log('Generating marker HTML for icon URL:', iconUrl);
        return `
        <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        width: 12px;
        height: 14px;
        ">
        <img src="${iconUrl}" style="
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
        " alt="legend-icon" />
        </div>`;
    }

    private getLineHtml(color: string, weight: number = 3): string {
        return `
        <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        style="display: inline-block; vertical-align: middle; opacity: 0.7;"
        >
        <line
            x1="0" y1="10"
            x2="12" y2="2"
            stroke="${color}"
            stroke-width="${weight}"
            stroke-linecap="round"
        />
        </svg>`;
    }

    private createPulsingIcon(colour: string, durationSeconds: number) {
        // We inject CSS variables into the inline style of the div
        const style = `
                --marker-color: ${colour};
                --pulse-rate: ${durationSeconds}s;
            `;

        return L.divIcon({
            className: 'custom-div-icon',
            // Inject the style string into the HTML
            html: `<div class="pulsing-marker" style="${style}"></div>`,
            iconSize: [20, 20],
            iconAnchor: [10, 10]
        });
    }

    private parseStringForMetaAdvanced(text: string | number, s?: SharedSeries, p?: SharedPoint): string | number {
        // populate the latest point if not provided
        if (p == null && s && s.values.length > 0) {
            p = s.values[s.values.length - 1];
        }

        // don't tag match if not a string
        if (!text || typeof text !== 'string') {
            return text;
        }

        // The 'g' flag ensures we find every instance of {tag} in the string
        return text.replace(/\{(.+?)\}/g, (fullMatch, tag) => {

            // 1. Lookup based on deep path (e.g., {user.name})
            if (tag.includes(".")) {
                const value = tag.split('.').reduce((acc, part) => acc && acc[part], this.metaDataDictionary);
                if (value !== undefined) {
                    return String(value);
                } else {
                    console.warn(`Meta tag '${tag}' not found in metadata dictionary.`);
                    return fullMatch; // Leave the unparsed tag in the string so the error is visible
                }
            }

            // 2. Check latest point
            if (p?.metaData && tag in p.metaData) {
                const value = p.metaData[tag];
                if (value !== undefined) {
                    return String(value);
                }
            }

            // 3. Check series
            if (s?.metaData && tag in s.metaData) {
                const value = s.metaData[tag];
                if (value !== undefined) {
                    return String(value);
                }
            }

            // Fallback if the tag is completely unmatched
            console.warn(`Meta tag '${fullMatch}' not found in any data source.`);
            return fullMatch;
        });
    }

}
